import type { Archetype } from './types';

export interface ArchetypeDef {
  id: Archetype;
  label: string;
  /** Hue accent for the star + axis label. */
  accent: string;
  /** Short one-line essence. */
  essence: string;
  /** First paragraph — what this archetype's submission feels like. */
  feels: string;
  /** Second paragraph — what it looks like in daily practice. */
  practice: string;
}

export const ARCHETYPES: Record<Archetype, ArchetypeDef> = {
  servant: {
    id: 'servant',
    label: 'the servant',
    accent: '#E0C896',
    essence: 'submission through care, attention, and the giving itself',
    feels:
      'for the servant, submission lives in the small acts no one will see. usefulness is the language. when something is poured perfectly, when a chore is done without being asked, when his comfort is composed around him — that is the version of devotion that feels most yourself. the praise lands less in the words and more in the noticing.',
    practice:
      'in daily life the servant gravitates toward acts of care. tea poured, clothes laid out, dinner timed. the risk is doing too much and asking for nothing — service uncoupled from reciprocity can quietly become resentment. watch for that. true service deepens when both partners can see it, name it, and let it count.',
  },
  devotee: {
    id: 'devotee',
    label: 'the devotee',
    accent: '#FFB347',
    essence: 'submission as reverence, ritual, sacred attention',
    feels:
      'for the devotee, submission becomes sacred. ritual language, thresholds, postures all carry weight. kneeling is not performance; it is coming home. the dynamic feels less like a contract and more like a practice — something that wants ceremony, repeated phrases, slow time. the body becomes a place of devotion.',
    practice:
      'in daily life the devotee builds small ceremonies: a greeting, a posture, a phrase said only here. the risk is that the practice becomes brittle without the ritual scaffolding — finding flexibility inside a sacred frame matters. let the devotion adapt; let it survive ordinary days when the candle is not lit.',
  },
  sensualist: {
    id: 'sensualist',
    label: 'the sensualist',
    accent: '#E8AAB6',
    essence: 'submission through the body, sensation as gateway',
    feels:
      'for the sensualist, the body is the doorway. a hand at the throat settles more than a long conversation. the weight of rope, the sting of a palm, the slow draw of silk — these speak fluently and quickly. submission arrives through skin first; the mind follows. words can be heavy here; touch is lighter than language.',
    practice:
      'in daily life the sensualist needs sensation woven through the dynamic — texture, temperature, pressure, presence. the risk is mistaking the intensity for the connection. sensation without context becomes performance. let the body be the language, but let it speak about something specific to the two of you.',
  },
  primal: {
    id: 'primal',
    label: 'the primal',
    accent: '#E0626A',
    essence: 'submission as instinct, chase, embodied animal energy',
    feels:
      'for the primal, submission is older than words. the body remembers being chased; being claimed feels older than being chosen. negotiation lives outside the moment; surrender lives inside it. when his weight pins you, when his hand finds your hair, the mind quiets and something simpler takes over. it is not a thought.',
    practice:
      'in daily life the primal needs a partner whose body knows what yours does. heavy negotiation followed by light play. the risk is letting the intensity skip past safety scaffolding — primal play needs more aftercare than it advertises. set the structure when not in scene so the scene can feel structureless.',
  },
  brat: {
    id: 'brat',
    label: 'the brat',
    accent: '#F5E6D3',
    essence: 'submission earned through play and resistance',
    feels:
      'for the brat, the surrender is sweeter when it has been earned. easy compliance bores. the fight is the foreplay; the back-and-forth is the connection. submission arrives through being held, contained, and out-paced — not through being asked nicely. the dominant who can hold you without flinching when you push is the one your nervous system trusts.',
    practice:
      'in daily life the brat needs a partner who is steady through the storm. light versions of resistance built into rituals (you reach for the thing you were told not to; he laughs and moves it). the risk is that the play becomes a test that no one passes. let the brat practice losing on purpose sometimes — the resistance is most meaningful when surrender is the actual destination.',
  },
  little: {
    id: 'little',
    label: 'the little',
    accent: '#C8B89A',
    essence: 'submission through gentle regression and caregiving dynamics',
    feels:
      'for the little, the world is heavy and there is a way to put it down for a while. tenderness with structure is a flavor you may have rarely tasted enough of. being told what to wear is a gift, not a chore. being cared for in a softer way is something you want more often than you let yourself admit. the dynamic creates a holding space.',
    practice:
      'in daily life the little benefits from a caregiver-shaped partner — someone who delights in the small daily acts of structuring. the risk is collapsing into the headspace so completely that adult life suffers. honor the headspace; protect the adult life. the little flourishes inside a dynamic where both versions of you are seen, not just the soft one.',
  },
};
